import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../../../axiosInstance';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTableComponent from '../../common/DataTable/PrimeDataTable';
import { convertUTCToLocal } from '../../utils/dateUtils';

const WorkflowVersions = ({ persona }) => {
  const { wfId } = useParams();
  const { t } = useTranslation();
  const [versionsData, setVersionsData] = useState([]);

  const navigate = useNavigate();

  const [paginationParams, setPaginationParams] = useState({
    limit: 10,
    offset: 0,
    sortField: 'createdAt',
    sortOrder: -1,
    filters: {},
  });

  const onDataTableEvent = useCallback((event) => {
    setPaginationParams((prev) => ({
      ...prev,
      limit: event.rows,
      offset: event.first,
      sortField: event.sortField || prev.sortField,
      sortOrder: event.sortOrder || prev.sortOrder,
    }));
  }, []);

  useEffect(() => {
    const fetchWorkflowVersions = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_SERVER_URL}/api/workflows/${wfId}/versions`,
          { withCredentials: true }
        );
        setVersionsData(response?.data?.WorkflowVersions || []);
      } catch (err) {
        console.error('Error fetching workflow versions:', err);
      }
    };

    if (wfId) {
      fetchWorkflowVersions();
    }
  }, [wfId]);

  const columns = useMemo(
    () => [
      {
        field: 'version',
        header: t('workflowVersions.version'),
        sortable: true,
        body: (rowData) => `V${rowData.version}`,
      },
      { field: 'name', header: t('workflowVersions.name'), sortable: true },
      {
        field: 'createdAt',
        header: t('Creation Date'),
        sortable: true,
        filter: true,
        body: (rowData) => convertUTCToLocal(rowData.createdAt),
      },
      {
        field: 'publishedAt',
        header: t('Published At'),
        sortable: true,
        filter: true,
        body: (rowData) => convertUTCToLocal(rowData.publishedAt),
      },
    ],
    [t]
  );

  const actions = useMemo(
    () => [
      {
        text: t('view'),
        color: 'primary',
        onClick: (row) =>
          navigate(
            `/${persona}/workflows/${row.wf_id}/versions/${row.id}/view`
          ),
      },
      {
        text: t('edit'),
        color: 'secondary',
        onClick: (row) =>
          navigate(`/${persona}/workflows/${row.wf_id}/versions/${row.id}`),
      },
    ],
    [t, navigate, persona]
  );

  return (
    <div>
      <DataTableComponent
        data={versionsData}
        columns={columns}
        actions={actions}
        onDataTableEvent={onDataTableEvent}
        paginationParams={paginationParams}
        totalRecords={versionsData?.length}
        enableCreate={false}
        enablePagination={true}
        enableLazyLoading={false}
        enableExport={true}
      />
    </div>
  );
};

export default WorkflowVersions;
